import { useEffect, useState } from "react";
import { ArrowUpRight } from "assets/ArrowUpRight";
import { ShootingStars } from "core/components/global/background/stars/shooting-stars";
import { StarsBackground } from "core/components/global/background/stars/stars-background";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@mui/material";
import { RedirectInformationBlock } from "./siteRedirect.styled";

export default function SiteRedirect() {
  const { url } = useParams();
  const navigate = useNavigate();
  const [decodedUrl, setDecodedUrl] = useState("");
  const [isSafe, setIsSafe] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [isProcessing, setIsProcessing] = useState(true);
  const [redirectInProgress, setRedirectInProgress] = useState(true);

  // Comprehensive URL validation and sanitization
  const validateAndSanitizeUrl = (input: string): { isValid: boolean; sanitizedUrl: string } => {
    try {
      // Remove control characters, null bytes, and unusual whitespace
      const cleaned = input
        .replace(/[\x00-\x1F\x7F-\x9F]/g, '') // Remove control characters
        .replace(/[\u200B-\u200D\uFEFF]/g, '') // Remove zero-width spaces
        .trim();

      // Must start with / and not //
      if (!cleaned.startsWith('/') || cleaned.startsWith('//')) {
        return { isValid: false, sanitizedUrl: '/' };
      }

      // Block backslashes (Windows path separators used in bypass attempts)
      if (cleaned.includes('\\')) {
        return { isValid: false, sanitizedUrl: '/' };
      }

      // Block URLs with protocol schemes
      if (/^\/[a-z][a-z0-9+.-]*:/i.test(cleaned)) {
        return { isValid: false, sanitizedUrl: '/' };
      }

      // Normalize path to resolve . and .. segments
      const pathParts = cleaned.split(/[?#]/)[0].split('/').filter(Boolean);
      const normalizedParts: string[] = [];
      
      for (const part of pathParts) {
        if (part === '..') {
          normalizedParts.pop(); // Go up one level
        } else if (part !== '.') {
          normalizedParts.push(part);
        }
      }

      // Reconstruct the URL with query string and hash if present
      const pathOnly = '/' + normalizedParts.join('/');
      const queryStart = cleaned.indexOf('?');
      const hashStart = cleaned.indexOf('#');
      
      let finalUrl = pathOnly;
      if (queryStart !== -1) {
        const query = hashStart !== -1 
          ? cleaned.substring(queryStart, hashStart)
          : cleaned.substring(queryStart);
        
        // Validate query string doesn't contain redirect bypasses
        if (!/\/\//.test(query)) {
          finalUrl += query;
        }
      }
      if (hashStart !== -1) {
        finalUrl += cleaned.substring(hashStart);
      }

      // Final check: must be a valid relative path
      if (!finalUrl.startsWith('/') || finalUrl.startsWith('//')) {
        return { isValid: false, sanitizedUrl: '/' };
      }

      return { isValid: true, sanitizedUrl: finalUrl };
    } catch (error) {
      return { isValid: false, sanitizedUrl: '/' };
    }
  };

  // Decode URL when component mounts
  useEffect(() => {
    if (url) {
      try {
        const decoded = decodeURIComponent(url);
        const { isValid, sanitizedUrl } = validateAndSanitizeUrl(decoded);
        
        if (isValid) {
          setDecodedUrl(sanitizedUrl);
          setIsSafe(true);
          document.title = `Redirecting to ${sanitizedUrl}`;
        } else {
          console.warn("Blocked potentially unsafe redirect:", decoded);
          setDecodedUrl("/");
          setIsSafe(false);
        }
      } catch (error) {
        console.error("URL decoding error:", error);
        setDecodedUrl("/");
        setIsSafe(false);
      }
    }
  }, [url, navigate]);

  // Set up countdown and redirect logic
  useEffect(() => {
    if (decodedUrl && redirectInProgress && isSafe) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            // Perform the redirect after countdown
            window.open(decodedUrl, "_self");
            setIsProcessing(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [decodedUrl, redirectInProgress, isSafe]);

  // Cancel redirect and go back
  const handleCancel = () => {
    setRedirectInProgress(false);
  };

  // Extract domain name for display
  const displayDomain = decodedUrl
    ? new URL(decodedUrl).hostname
    : "external site";

  return (
    <div
      className="h-100 bg-neutral-900 flex flex-col items-center justify-center relative w-full"
      onClick={handleCancel}
    >
      <div className="relative flex-col md:flex-row z-10 text-3xl md:text-5xl md:leading-tight max-w-5xl mx-auto text-center tracking-tight font-medium bg-clip-text text-transparent bg-gradient-to-b from-neutral-800 via-white to-white flex items-center gap-2 md:gap-8">
        <RedirectInformationBlock className="p-8 rounded-lg flex flex-col items-center justify-center text-center">
          <div className="relative w-16 h-16 mb-4">
            <div className={`absolute inset-0 border-t-4 border-blue-500 rounded-full ${(redirectInProgress && isProcessing) && "animate-spin" }`}></div>
          </div>
          <h3 className="text-lg font-semibold mb-2 text-white">Opening Internal Page</h3>
          {isSafe ? (
            isProcessing ? (
              <p className="text-sm text-white">
                Redirecting to {decodedUrl} in {countdown} seconds...
              </p>
            ) : (
              <p className="text-sm text-white">
                Your page should have redirected to {decodedUrl}
              </p>
            )
          ) : (
            <p className="text-sm text-red-400">Invalid or unsupported redirect URL.</p>
          )}
          <div className="flex gap-3 mt-6">
            {isSafe ? (
              <Link
                className="MuiButtonBase-root MuiButton-root MuiButton-text MuiButton-textPrimary MuiButton-sizeMedium MuiButton-textSizeMedium MuiButton-colorPrimary MuiButton-root MuiButton-text MuiButton-textPrimary MuiButton-sizeMedium MuiButton-textSizeMedium MuiButton-colorPrimary px-4 py-2 text-white rounded-md hover:bg-blue-600 css-f7iyy0-MuiButtonBase-root-MuiButton-root"
                to={decodedUrl}
              >
                <p className="text-white">Continue Now</p>
              </Link>
            ) : null}
            <Button
              className="px-4 py-2 border-gray-100 text-white rounded-md hover:bg-red-800"
              onClick={(e) => {
                e.stopPropagation();
                handleCancel();
              }}
            >
              Cancel
            </Button>
          </div>
        </RedirectInformationBlock>
      </div>
      <ShootingStars />
      <StarsBackground />
    </div>
  );
}
